import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IoCheckmark } from 'react-icons/io5';
import { useThemeContext } from '../contexts/ThemeContext';

const LanguagePage = () => {
	const { t, i18n } = useTranslation();


	const navigate = useNavigate();
	const { theme } = useThemeContext();
	const [selectedLanguage, setSelectedLanguage] = useState(() => localStorage.getItem('app_language') || 'ru');

	const languages = [
		{ code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
		{ code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
		{ code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
		{ code: 'uz', name: 'Uzbek', nativeName: 'O\'zbekcha', flag: '🇺🇿' },
		{ code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
		{ code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
		{ code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
		{ code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
		{ code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷' },
		{ code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
		{ code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycan', flag: '🇦🇿' },
		{ code: 'ge', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪' },
		{ code: 'kk', name: 'Kazakh', nativeName: 'Қазақ', flag: '🇰🇿' },
		{ code: 'ky', name: 'Kyrgyz', nativeName: 'Кыргызча', flag: '🇰🇬' },
		{ code: 'tj', name: 'Tajik', nativeName: 'Тоҷикӣ', flag: '🇹🇯' },
		{ code: 'mn', name: 'Mongolian', nativeName: 'Монгол', flag: '🇲🇳' },
		{ code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰' },
		{ code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
		{ code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
		{ code: 'kr', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
		{ code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' }
	];

	const handleLanguageSelect = (langCode) => {
		setSelectedLanguage(langCode);
		i18n.changeLanguage(langCode);
		localStorage.setItem('app_language', langCode);

		// Вибрация при смене языка
		if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.HapticFeedback) {
			window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
		}
	};



	// Telegram кнопка назад и вибрация при загрузке
	useEffect(() => {
		if (window.Telegram && window.Telegram.WebApp) {
			window.Telegram.WebApp.BackButton.show();
			window.Telegram.WebApp.BackButton.onClick(() => navigate(-1));

			// Вибрация при загрузке страницы
			if (window.Telegram.WebApp.HapticFeedback) {
				window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
			}
		}
		return () => {
			if (window.Telegram && window.Telegram.WebApp) {
				window.Telegram.WebApp.BackButton.hide();
				window.Telegram.WebApp.BackButton.offClick();
			}
		};
	}, [navigate]);

	return (
		<Container theme={theme}>
			<Header>
				<Title>{t('profile.language')}</Title>
			</Header>

			<LanguageList>
				{languages.map((lang) => (
					<LanguageItem
						key={lang.code}
						onClick={() => handleLanguageSelect(lang.code)}
						isSelected={selectedLanguage === lang.code}
						theme={theme}
					>
						<LanguageInfo>
							<Flag>{lang.flag}</Flag>
							<LanguageNameContainer>
								<LanguageName>{lang.name}</LanguageName>
								<NativeLanguageName>{lang.nativeName}</NativeLanguageName>
							</LanguageNameContainer>
						</LanguageInfo>
						{selectedLanguage === lang.code && (
							<CheckIcon>
								<IoCheckmark size={20} />
							</CheckIcon>
						)}
					</LanguageItem>
				))}
			</LanguageList>
		</Container>
	);
};

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	padding: 0 0 16px 0;
	margin-bottom: 24px;
	position: relative;
`;

const Title = styled.h1`
	font-size: 20px;
	font-weight: 700;
	color: ${props => props.theme.text};
	margin: 0;
	text-align: center;
	flex: 1;
`;

const LanguageList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 20px;
`;

const LanguageItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px;
	background: ${props => props.theme.cardBackground};
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.2s;
	border: 2px solid ${props => props.isSelected ? props.theme.primary : 'transparent'};

	&:hover {
		background: ${props => props.theme.cardBackgroundHover};
	}
`;

const LanguageInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const Flag = styled.span`
	font-size: 24px;
`;

const LanguageNameContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const LanguageName = styled.span`
	font-size: 16px;
	font-weight: 500;
	color: ${props => props.theme.text};
`;

const NativeLanguageName = styled.span`
	font-size: 14px;
	color: ${props => props.theme.textSecondary || '#888'};
`;

const CheckIcon = styled.div`
	color: ${props => props.theme.primary};
	display: flex;
	align-items: center;
	justify-content: center;
`;

export default LanguagePage; 